// Copyright ©️ 2026 eVolve MEP, LLC

import { useState, Fragment, useMemo } from 'react';

import { type HexTriplet, FLAT_BLACK_FILLER } from '@evolvemep/api-ts';
import { Card, ColorSwatch, Divider, Flex, LoadingOverlay, Stack, Text } from '@mantine/core';

import { createColorScheme } from 'helpers/createColorScheme';
import { isNil } from 'helpers/isNotNil';
import type { AnalyticsViewProps } from 'modules/Shop/Insights/RevitUsage/types';
import {
  InstalledEvolveVersionsChart,
  type InstalledVersionsData,
} from 'modules/Shop/Insights/RevitUsage/views/charts/InstalledEvolveVersionsChart';

const UserBreakdown = ({
  data,
  loading,
  installedVersionsMap,
}: {
  data: InstalledVersionsData[] | undefined;
  loading: boolean;
  installedVersionsMap: Record<InstalledVersionsData['version'], HexTriplet>;
}) => (
  <Stack style={{ overflowY: 'auto' }}>
    {data?.map(({ version, emails }) => (
      <Fragment key={version}>
        <Flex direction="column">
          <Flex gap="xs" align="center">
            <ColorSwatch withShadow={false} size={16} color={installedVersionsMap[version] ?? FLAT_BLACK_FILLER} />

            <Text fw={600}>{version}</Text>
          </Flex>
          {emails.map((email) => (
            <Text key={email} fz="sm" c="dimmed">
              {email}
            </Text>
          ))}
        </Flex>
        <Divider />
      </Fragment>
    ))}
    <LoadingOverlay visible={loading} />
  </Stack>
);

export const InstalledVersionsView = (props: AnalyticsViewProps) => {
  const [data, setData] = useState<InstalledVersionsData[]>();

  const [
    // AG Charts pie charts loop through a list of colors (in order) when picking colors
    // so we generate a color per slice.
    installedVersionsFills,
    // However, we also want to make sure the user list on the right pane
    // picks the proper color for the version the user uses.
    installedVersionsMap,
  ] = useMemo(() => {
    const fills = createColorScheme(data?.length ?? 10);
    const mapped =
      data?.reduce(
        (out, curr, i) => ({
          ...out,
          [curr.version]: fills[i] ?? FLAT_BLACK_FILLER,
        }),
        {} as Record<InstalledVersionsData['version'], HexTriplet>,
      ) ?? {};
    return [fills, mapped] as const;
  }, [data]);

  return (
    <Flex gap="sm" h="100%" style={{ overflowY: 'auto' }}>
      <Card withBorder flex={5}>
        <InstalledEvolveVersionsChart
          onDataRealized={setData}
          installedVersionsFills={installedVersionsFills}
          {...props}
        />
      </Card>
      <Card withBorder flex={2}>
        <UserBreakdown data={data} installedVersionsMap={installedVersionsMap} loading={isNil(data)} />
      </Card>
    </Flex>
  );
};
